import { apiClient } from './client';
import type { Coupon,Customer, CustomerAttribute } from './types';

export const customersApi = {
  async search(params: {
    query?: string;
    email?: string;
    phone?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    customers: Customer[];
    pagination: { page: number; limit: number; total: number };
  }> {
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.set('query', params.query);
    if (params.email) searchParams.set('email', params.email);
    if (params.phone) searchParams.set('phone', params.phone);
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const path = queryString ? `/customers/search?${queryString}` : '/customers/search';

    return apiClient.request(path);
  },

  async getDetails(customerId: number): Promise<Customer> {
    const response = await apiClient.request<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      phone?: string;
      store_credit_amounts?: Array<{ amount: number }>;
      customer_group_id?: number;
      date_created: string;
      addresses?: unknown[];
      orders?: { totalCount: number; edges: Array<{ node: { totalIncTax: { value: number } } }> };
    }>(`/customers/${customerId}`);

    const storeCredit = response.store_credit_amounts?.[0]?.amount || 0;
    const totalSpent =
      response.orders?.edges.reduce((sum, e) => sum + (e.node.totalIncTax?.value || 0), 0) || 0;

    return {
      id: response.id,
      email: response.email,
      firstName: response.first_name,
      lastName: response.last_name,
      phone: response.phone,
      storeCredit,
      createdAt: response.date_created,
      orderCount: response.orders?.totalCount,
      totalSpent,
    };
  },

  async getAttributes(): Promise<{ attributes: CustomerAttribute[] }> {
    const response = await apiClient.request<{
      attributes: Array<{
        id: number;
        name: string;
        attribute_type: string;
      }>;
    }>('/customers/attributes');

    return {
      attributes: response.attributes.map(a => ({
        id: a.id,
        name: a.name,
        type: a.attribute_type,
      })),
    };
  },

  async searchByAttribute(
    attributeId: number,
    value: string
  ): Promise<{ customers: Customer[] }> {
    return apiClient.request(
      `/customers/search/attribute?attributeId=${attributeId}&value=${encodeURIComponent(value)}`
    );
  },

  async getStoreCredit(customerId: number): Promise<{ balance: number; currency: string }> {
    return apiClient.request(`/storecredit/${customerId}`);
  },

  async applyStoreCredit(
    customerId: number,
    amount: number
  ): Promise<{
    success: boolean;
    newBalance: number;
    amountApplied: number;
    transactionId?: string;
  }> {
    return apiClient.request('/storecredit/apply', {
      method: 'POST',
      body: { customerId, amount },
    });
  },
};

export const couponsApi = {
  async list(enabledOnly = false): Promise<{ coupons: Coupon[] }> {
    const path = enabledOnly ? '/coupons?enabledOnly=true' : '/coupons';
    return apiClient.request<{ coupons: Coupon[] }>(path);
  },

  async validate(code: string): Promise<{
    valid: boolean;
    coupon?: Coupon;
    error?: string;
  }> {
    return apiClient.request('/coupons/validate', {
      method: 'POST',
      body: { code },
    });
  },
};

export interface CreateGiftCardInput {
  toName: string;
  toEmail: string;
  fromName: string;
  fromEmail: string;
  amount: number;
  code?: string;
  message?: string;
  expiryDate?: string;
}

export interface GiftCard {
  id: number;
  code: string;
  amount: number;
  balance: number;
  status: string;
  toName: string;
  toEmail: string;
  fromName: string;
  fromEmail: string;
  currencyCode: string;
  expiryDate?: string;
}

export const giftCardsApi = {
  async checkBalance(code: string): Promise<{
    found: boolean;
    valid?: boolean;
    balance?: number;
    currencyCode?: string;
    message?: string;
  }> {
    return apiClient.request('/giftcards/check', {
      method: 'POST',
      body: { code },
    });
  },

  async redeem(
    code: string,
    amount: number
  ): Promise<{
    redeemed: boolean;
    amountUsed: number;
    previousBalance: number;
    newBalance: number;
    currencyCode: string;
  }> {
    return apiClient.request('/giftcards/redeem', {
      method: 'POST',
      body: { code, amount },
    });
  },

  async create(input: CreateGiftCardInput): Promise<GiftCard> {
    return apiClient.request('/giftcards/create', {
      method: 'POST',
      body: input,
    });
  },
};
